import type {
  Hypothesis,
  HypothesisCreateBody,
  HypothesisPatchBody,
  HypothesisRun,
  HypothesisStatus,
  SavedResearchReport,
} from '@/types/hypothesis'
import type { HypothesisFinding } from '@/types/hypothesis-finding'
import type { Investigation } from '@/types/investigation'
import { apiClient } from './client'

export const hypothesesApi = {
  list: (caseId: string, statuses?: HypothesisStatus[]) => {
    const qs = statuses && statuses.length > 0 ? `?status=${statuses.join(',')}` : ''
    return apiClient.get<Hypothesis[]>(`/v1/cases/${caseId}/hypotheses${qs}`)
  },

  get: (caseId: string, id: string) =>
    apiClient.get<Hypothesis>(`/v1/cases/${caseId}/hypotheses/${id}`),

  create: (caseId: string, body: HypothesisCreateBody) =>
    apiClient.post<Hypothesis>(`/v1/cases/${caseId}/hypotheses`, body),

  update: (caseId: string, id: string, patch: HypothesisPatchBody) =>
    apiClient.patch<Hypothesis>(`/v1/cases/${caseId}/hypotheses/${id}`, patch),

  remove: (caseId: string, id: string) =>
    apiClient.delete<void>(`/v1/cases/${caseId}/hypotheses/${id}`),

  // ── Runs ──────────────────────────────────────────────────────────────────

  listRuns: (caseId: string, hypothesisId: string) =>
    apiClient.get<HypothesisRun[]>(`/v1/cases/${caseId}/hypotheses/${hypothesisId}/runs`),

  getRun: (caseId: string, hypothesisId: string, runId: string) =>
    apiClient.get<HypothesisRun>(`/v1/cases/${caseId}/hypotheses/${hypothesisId}/runs/${runId}`),

  triggerRun: (caseId: string, hypothesisId: string) =>
    apiClient.post<HypothesisRun>(`/v1/cases/${caseId}/hypotheses/${hypothesisId}/runs`, {}),

  // ── Findings (run-scoped artifacts) ───────────────────────────────────────

  listFindings: (caseId: string, hypothesisId: string, runId: string) =>
    apiClient.get<HypothesisFinding[]>(
      `/v1/cases/${caseId}/hypotheses/${hypothesisId}/runs/${runId}/findings`
    ),

  promoteFinding: (caseId: string, hypothesisId: string, runId: string, findingId: string) =>
    apiClient.post<Investigation>(
      `/v1/cases/${caseId}/hypotheses/${hypothesisId}/runs/${runId}/findings/${findingId}/promote`,
      {}
    ),

  /** *Uložiť report* (PRD-0076): save the run's report as its investigation, or refresh the
   *  one an earlier save created. */
  saveReport: (caseId: string, hypothesisId: string, runId: string) =>
    apiClient.post<SavedResearchReport>(
      `/v1/cases/${caseId}/hypotheses/${hypothesisId}/runs/${runId}/report`,
      {}
    ),

  /** Build the SSE URL for a run's progress events. The frontend opens an
   *  EventSource against this URL; auth is via the existing session cookie. */
  runEventsUrl: (caseId: string, hypothesisId: string, runId: string) =>
    `/api/v1/cases/${caseId}/hypotheses/${hypothesisId}/runs/${runId}/events`,
}
